// src/components/StyledComponents/CircleImageWithText.tsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import preventDefaultEvents from '../hoc/preventDefaultEvents';

const rotate = keyframes`to { transform: rotate(360deg); }`;
const BRAND_TITLE_SHORT_FALLBACK = 'אליאור מור';

type IntrinsicSize = { width: number; height: number };

interface Props {
  src: string;
  alt?: string;
  text?: string;             
  spinSeconds?: number;     
  intrinsicSize?: IntrinsicSize;
  priority?: boolean;
  className?: string;
  srcSet?: string;
  sizes?: string;


  textGradient?: {
    from: string;
    via?: string;
    to: string;
    angle?: number; 
  };


  idPrefix?: string;
}

const Figure = styled.figure.attrs({
  itemScope: true,
  itemType: 'https://schema.org/ImageObject',
})`
  --size: ${({ theme }) => theme?.sizes?.small?.circleImage ?? '220px'};
  --ring: 6px;
  --orbit-pad: clamp(18px, calc(var(--size) / 8), 52px);
  --orbit-band: clamp(10px, calc(var(--size) / 16), 22px);
  --text-gap: clamp(6px, calc(var(--size) / 40), 12px);

  margin: 0;
  position: relative;
  display: inline-block;

  width: var(--size);
  height: var(--size);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  transform: translateZ(0);

  overflow: visible;
  contain: layout;

  @media (max-width: 768px) and (max-height: 700px) {
    --orbit-pad: clamp(14px, calc(var(--size) / 10), 40px);
    --orbit-band: clamp(8px,  calc(var(--size) / 18), 18px);
    --text-gap: clamp(5px,  calc(var(--size) / 46), 10px);
  }
`;

const PhotoFrame = styled.div`
  position: absolute;
  inset: var(--ring);
  border-radius: 50%;
  overflow: hidden;
  isolation: isolate;
`;

const PhotoImg = styled.img.attrs<{ $priority?: boolean }>((p) => ({
  decoding: 'async',
  loading: p.$priority ? 'eager' : 'lazy',
  fetchpriority: p.$priority ? 'high' : 'auto', 
}))`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
  transform: translateZ(0) scale(1.0015);
  will-change: transform;
  backface-visibility: hidden;
  border-radius: 50%;
`;

const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: inset 0 0 0 0.75px rgba(255,255,255,0.12);
  pointer-events: none;
`;

const Ring = styled.svg.attrs({ 'aria-hidden': true, focusable: 'false' })`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  filter: drop-shadow(0 8px 18px rgba(0,0,0,0.35));
`;

const WhiteBand = styled.svg.attrs({ 'aria-hidden': true, focusable: 'false' })`
  position: absolute;
  left: calc(-1 * var(--orbit-pad));
  top:  calc(-1 * var(--orbit-pad));
  width:  calc(var(--size) + 2 * var(--orbit-pad));
  height: calc(var(--size) + 2 * var(--orbit-pad));
  pointer-events: none;
  overflow: visible;
  z-index: 0;
`;

const TextOrbit = styled.svg.attrs({
  'aria-hidden': true,
  focusable: 'false',
  xmlnsXlink: 'http://www.w3.org/1999/xlink',
})`
  position: absolute;
  left: calc(-1 * var(--orbit-pad));
  top:  calc(-1 * var(--orbit-pad));
  width:  calc(var(--size) + 2 * var(--orbit-pad));
  height: calc(var(--size) + 2 * var(--orbit-pad));

  pointer-events: none;
  will-change: transform;
  transform-origin: 50% 50%;
  animation: ${rotate} 18s linear infinite;
  backface-visibility: hidden;
  z-index: 1;
  color: ${({ theme }) => theme.titleColor};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

text {
  font-family: ${({ theme }) =>
    `${theme?.fonts?.rubikExtraBold ?? ''}, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif`};
  font-size: clamp(12px, calc(var(--size) / 18 + 2px), 16px);
  letter-spacing: 0.3px;
  alignment-baseline: middle;
  dominant-baseline: middle;
  paint-order: stroke fill;
  stroke: rgba(0,0,0,0.4);
  stroke-width: 0.6px;
  vector-effect: non-scaling-stroke;
}
`;

const VisuallyHiddenCaption = styled.figcaption`
  position: absolute !important;
  height: 1px; width: 1px;
  overflow: hidden;
  clip: rect(1px,1px,1px,1px);
  white-space: nowrap;
`;

const Meta = ({ src }: { src: string }) => (
  <>
    <meta itemProp="thumbnailUrl" content={src} />
    <meta itemProp="url" content={src} />
  </>
);

const PATH_LENGTH = 1000;
const WHITE_BAND_RADIUS = 86;
const WHITE_BAND_STROKE = 1;
const TEXT_GAP = 6;
const TEXT_RADIUS = WHITE_BAND_RADIUS + WHITE_BAND_STROKE / 2 + TEXT_GAP + 4;

const clampAngle = (n?: number) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return 135;
  const a = n % 360;
  return a < 0 ? a + 360 : a;
};

const sanitizeId = (s: string) => {
  const cleaned = String(s).replace(/[^a-zA-Z0-9_-]/g, '');
  return cleaned.length ? cleaned : 'circle';
};

const CircleImageWithText: React.FC<Props> = React.memo(
  ({
    src,
    alt,
    text = ' ★  DJ ELIOR MOR  ★  מוזיקה לאירועים  ★  ',
    spinSeconds = 18,
    intrinsicSize = { width: 512, height: 512 },
    priority = false,
    className,
    srcSet,
    sizes,
    textGradient,
    idPrefix = 'circle',
  }) => {
    const fallbackAlt = `${BRAND_TITLE_SHORT_FALLBACK} – די ג׳יי לאירועים`;
    const finalAlt = alt && alt.trim().length > 0 ? alt : fallbackAlt;

    const safePrefix = React.useMemo(() => sanitizeId(idPrefix), [idPrefix]);
    const orbitPathId = `${safePrefix}_orbit`;
    const gradId = `${safePrefix}_grad`;

    const g = {
      from: textGradient?.from ?? '#ffffff',
      via: textGradient?.via ?? '#a78bfa',
      to: textGradient?.to ?? '#22d3ee',
      angle: clampAngle(textGradient?.angle),
    };

    const rad = (g.angle * Math.PI) / 180;
    const vx = Math.cos(rad);
    const vy = Math.sin(rad);

    const cx = 100;
    const cy = 100;
    const L = 120; 

    const gx1 = cx - vx * L;
    const gy1 = cy - vy * L;
    const gx2 = cx + vx * L;
    const gy2 = cy + vy * L;

    return (
      <Figure className={className} itemProp="image">
        <Meta src={src} />

        <PhotoFrame>
        <PhotoImg
              src={src}
              alt={finalAlt}
              width={intrinsicSize.width}
              height={intrinsicSize.height}
              $priority={priority}
              {...(srcSet ? { srcSet } : {})}
              {...(sizes ? { sizes } : {})}
            />
          <PhotoOverlay />
        </PhotoFrame>

        <Ring viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="currentColor" />
            </linearGradient>
            <radialGradient id="softEdge" cx="50%" cy="50%" r="50%">
              <stop offset="80%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="49"
            fill="none"
            stroke="url(#ringGrad)"
            strokeWidth="2"
            style={{ color: 'currentColor' }}
          />
          <circle cx="50" cy="50" r="46" fill="url(#softEdge)" opacity="0.12" />
        </Ring>

        <WhiteBand viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet">
          <circle
            cx="100"
            cy="100"
            r={WHITE_BAND_RADIUS}
            fill="none"
            stroke="#FFFFFF"
            strokeWidth={WHITE_BAND_STROKE}
            strokeLinecap="round"
          />
        </WhiteBand>

        <TextOrbit
          viewBox="0 0 200 200"
          preserveAspectRatio="xMidYMid meet"
          style={{ animationDuration: `${spinSeconds}s` }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id={gradId}
              gradientUnits="userSpaceOnUse"
              x1={gx1}
              y1={gy1}
              x2={gx2}
              y2={gy2}
            >
              <stop offset="0%" stopColor={g.from} />
              {g.via ? <stop offset="50%" stopColor={g.via} /> : null}
              <stop offset="100%" stopColor={g.to} />
            </linearGradient>

            <path
              id={orbitPathId}
              d={`M100,100 m -${TEXT_RADIUS},0 a ${TEXT_RADIUS},${TEXT_RADIUS} 0 1,1 ${
                TEXT_RADIUS * 2
              },0 a ${TEXT_RADIUS},${TEXT_RADIUS} 0 1,1 -${TEXT_RADIUS * 2},0`}
              pathLength={PATH_LENGTH}
            />
          </defs>

          <text textAnchor="middle" fill="currentColor">
            <textPath
              href={`#${orbitPathId}`}
              xlinkHref={`#${orbitPathId}`}
              startOffset="50%"
              lengthAdjust="spacingAndGlyphs"
              pathLength={PATH_LENGTH}
              fill="currentColor"
            >
              <tspan dy=".35em" fill="currentColor">
                {text.repeat(3)}
              </tspan>
            </textPath>
          </text>

          <text textAnchor="middle" fill={`url(#${gradId})`}>
            <textPath
              href={`#${orbitPathId}`}
              xlinkHref={`#${orbitPathId}`}
              startOffset="50%"
              lengthAdjust="spacingAndGlyphs"
              pathLength={PATH_LENGTH}
              fill={`url(#${gradId})`}
            >
              <tspan dy=".35em" fill={`url(#${gradId})`}>
                {text.repeat(3)}
              </tspan>
            </textPath>
          </text>
        </TextOrbit>

        <VisuallyHiddenCaption>{finalAlt}</VisuallyHiddenCaption>
      </Figure>
    );
  }
);

export default preventDefaultEvents(CircleImageWithText);
